import CardSingleBlock from "./card-single-block";
import BlockText from "../block-content/block-text";
import { imageUrlFor } from "../../lib/image-url";
import { Link } from "gatsby";

/**
 * Horizontal Item
 * @param title
 * @param image
 * @param _rawBody
 * @returns {JSX.Element}
 * @constructor
 */
const HorizontalItem = ({ title, image, _rawBody, link, section }) => {
  const imageUrl =
    image && image.asset && image.asset._id
      ? imageUrlFor(image.asset._id)
          .auto("format")
          .width(637)
          .height(428)
          .quality(65)
          .url()
      : false;
  const titleClass = `text-blue-500 font-bold mb-1 md:text-lg${
    link ? " group-hover:text-blue-700 underline" : ""
  }`;

  return (
    <div className="bg-white md:flex-1 shadow-lg rounded mb-6 md:mb-0">
      <div className="object-contain">
        {imageUrl && (
          <img loading="lazy" src={imageUrl} alt={image.alt || ""} />
        )}
      </div>
      <div className="p-4">
        {title && <p className={titleClass}>{title}</p>}
        {_rawBody && (
          <div className="text-sm md:text-base text-gray-700">
            <BlockText blocks={_rawBody} section={section} />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * PageBlockGroup
 * @param pageBlockGroup
 * @returns {JSX.Element}
 * @constructor
 */
const PageBlockGroup = ({ pageBlockGroup, section }) => {
  if (
    pageBlockGroup.orientationType === "horizontal" ||
    pageBlockGroup.pageBlockType.includes("horizontal") ||
    pageBlockGroup.pageBlockType.includes("Horizontal")
  ) {
    return (
      <section
        className={`max-w-full py-6 md:py-10 px-4 ${
          pageBlockGroup?.layout?.background?.gradient ? "bg-gradient" : ""
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-start md:-mx-2">
            <div className="w-full">
              {pageBlockGroup.title && (
                <div className="md:mx-auto md:w-10/12">
                  <hr className="w-8 border-yellow-500 border-4 mb-2" />
                  <h2 className="text-blue-500 leading-tight font-bold tracking-tighter text-2xl md:text-3xl lg:text-4xl mb-3">
                    {pageBlockGroup.title}
                  </h2>
                </div>
              )}
              {pageBlockGroup._rawBody && (
                <div className="w-full text-sm md:text-xl mb-6 md:mb-10 w-full md:mx-auto md:w-10/12">
                  <BlockText
                    blocks={pageBlockGroup._rawBody}
                    section={section}
                  />
                </div>
              )}
              <ul className="md:flex md:flex-wrap md:-mx-4">
                {pageBlockGroup.pageBlocks &&
                  pageBlockGroup.pageBlocks.map((pageBlock) => (
                    <li
                      key={pageBlock._key}
                      className="w-full md:flex md:w-4/12 md:px-4 my-4"
                    >
                      {pageBlock.link &&
                        !pageBlock.link.reference &&
                        pageBlock.link.href && (
                          <a
                            href={pageBlock.link.href}
                            className="md:flex group hover:ring"
                          >
                            <HorizontalItem {...pageBlock} />
                          </a>
                        )}
                      {pageBlock.link &&
                        !pageBlock.link.href &&
                        pageBlock.link.reference &&
                        pageBlock.link.reference.path && (
                          <a
                            href={pageBlock.link.reference.path}
                            className="md:flex group hover:ring"
                          >
                            <HorizontalItem {...pageBlock} />
                          </a>
                        )}
                      {!pageBlock.link && (
                        <div className="md:flex group">
                          <HorizontalItem {...pageBlock} />
                        </div>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-10 md:py-16 bg-gradient">
      <div className="max-w-7xl px-4 mx-auto">
        <div className="flex flex-wrap">
          <div className="w-full lg:w-4/12 lg:pr-16 xl:pr-24">
            <hr className="w-8 border-yellow-500 border-4 mb-2" />
            <h2 className="text-blue-500 leading-tight font-bold tracking-tighter text-2xl md:text-3xl lg:text-4xl mb-3">
              {pageBlockGroup.title}
            </h2>
            <div className="mb-3 text-sm md:text-lg">
              {pageBlockGroup._rawBody && (
                <BlockText blocks={pageBlockGroup._rawBody} section={section} />
              )}
            </div>
          </div>
          <div className="w-full lg:w-8/12">
            <ul>
              {pageBlockGroup.pageBlocks &&
                pageBlockGroup.pageBlocks.map((pageBlock) => (
                  <CardSingleBlock
                    pageBlock={pageBlock}
                    alwaysShowImage={pageBlockGroup.alwaysShowImage}
                    key={pageBlock._key}
                    section={section}
                  />
                ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PageBlockGroup;
